import { useEffect, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";
import Icon from "../HOC/Icon";
import getIssue from "../../controllers/logger/getIssue";
import resolveIssue from "../../controllers/logger/resolveIssue";
import extendIssue from "../../controllers/logger/extendIssue";
import updateIssue from "../../controllers/logger/updateIssue";
import deleteIssue from "../../controllers/logger/deleteIssue";
import LoggerBadge from "./LoggerBadge";
import {
  environmentTone,
  orderLabel,
  statusCodeTone,
  statusTone,
} from "./loggerUtils";

export default function IssueDetailDrawer({
  issueId,
  open,
  onClose,
  onChanged,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [issue, setIssue] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open || !issueId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setIssue(null);
      const res = await getIssue(issueId);
      if (!active) return;
      setLoading(false);
      if (res.return) {
        setIssue(res.data);
        setNote(res.data?.resolutionNote || "");
      } else {
        enqueueSnackbar(res.msg || "Failed to load issue", { variant: "error" });
        onClose?.();
      }
    })();
    return () => {
      active = false;
    };
  }, [open, issueId]);

  async function handleResolve() {
    if (!issue?._id) return;
    setActing(true);
    const res = await resolveIssue(issue._id, note);
    setActing(false);
    if (res.return) {
      setIssue(res.data);
      enqueueSnackbar("Issue marked resolved", { variant: "success" });
      onChanged?.(res.data);
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  }

  async function handleReopen() {
    if (!issue?._id) return;
    setActing(true);
    const res = await updateIssue(issue._id, { status: "unresolved" });
    setActing(false);
    if (res.return) {
      setIssue(res.data);
      enqueueSnackbar("Issue reopened", { variant: "success" });
      onChanged?.(res.data);
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  }

  async function handleExtend() {
    if (!issue?._id) return;
    setActing(true);
    const res = await extendIssue(issue._id);
    setActing(false);
    if (res.return) {
      setIssue(res.data);
      enqueueSnackbar("Retention extended by 30 days", { variant: "success" });
      onChanged?.(res.data);
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  }

  async function handleDelete() {
    if (!issue?._id) return;
    if (!window.confirm("Delete this issue permanently?")) return;
    setActing(true);
    const res = await deleteIssue(issue._id);
    setActing(false);
    if (res.return) {
      enqueueSnackbar("Issue deleted", { variant: "success" });
      onChanged?.(null);
      onClose?.();
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  }

  const code = issue ? statusCodeTone(issue.statusCode) : null;
  const env = issue ? environmentTone(issue.environment) : null;
  const st = issue ? statusTone(issue.status) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: "16px", overflow: "hidden" },
      }}
    >
      <DialogTitle className="!flex !items-center !justify-between !pr-3 !bg-[#F8FAFC] !border-b !border-[#E8EEF8]">
        <span className="font-bold text-[#0F172A]">Issue detail</span>
        <IconButton onClick={onClose} size="small">
          <Icon icon="material-symbols:close" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="!p-0">
        {loading || !issue ? (
          <div className="flex justify-center py-16">
            <CircularProgress size={32} />
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="px-4 py-4 sm:px-6 border-b border-[#E8EEF8] space-y-3">
              <div className="flex flex-wrap gap-2">
                <LoggerBadge {...code}>{issue.statusCode}</LoggerBadge>
                <LoggerBadge {...st}>{st.label}</LoggerBadge>
                <LoggerBadge {...env}>{env.label}</LoggerBadge>
                <LoggerBadge bg="#F1F5F9" color="#334155">
                  {issue.method}
                </LoggerBadge>
                <LoggerBadge bg="#EEF2FF" color="#3730A3">
                  {issue.orderType}
                </LoggerBadge>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-[#64748B]">
                  Endpoint
                </div>
                <div className="font-semibold text-[#0F172A] break-all">
                  {issue.endpointCalled}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Meta label="Page" value={issue.page} />
                <Meta label="Route" value={issue.route || "—"} />
                <Meta label="Order" value={orderLabel(issue.order)} />
                <Meta
                  label="User"
                  value={
                    issue.user
                      ? `${issue.user.role || "—"}${issue.user.id ? ` · ${issue.user.id}` : ""}`
                      : "—"
                  }
                />
                <Meta
                  label="Created"
                  value={moment(issue.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                />
                <Meta
                  label="Expires"
                  value={moment(issue.expiresAt).format("YYYY-MM-DD HH:mm:ss")}
                />
              </div>
            </div>

            <div className="px-4 py-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <JsonBlock title="Payload" value={issue.payload} />
              <JsonBlock title="Error response" value={issue.errorResponse} />
              <div className="lg:col-span-2">
                <JsonBlock title="Order context" value={issue.order} />
              </div>
            </div>

            <div className="px-4 py-4 sm:px-6 border-t border-[#E8EEF8] space-y-3 bg-[#F8FAFC]">
              <TextField
                label="Resolution note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
              <div className="flex flex-wrap gap-2">
                {issue.status !== "resolved" ? (
                  <ActionButton
                    onClick={handleResolve}
                    disabled={acting}
                    primary
                  >
                    Mark resolved
                  </ActionButton>
                ) : (
                  <ActionButton onClick={handleReopen} disabled={acting}>
                    Reopen
                  </ActionButton>
                )}
                <ActionButton onClick={handleExtend} disabled={acting}>
                  Extend +30 days
                </ActionButton>
                <ActionButton onClick={handleDelete} disabled={acting} danger>
                  Delete
                </ActionButton>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[#64748B]">{label}</div>
      <div className="text-[#0F172A] break-all">{value}</div>
    </div>
  );
}

function JsonBlock({ title, value }) {
  return (
    <div className="rounded-xl border border-[#E8EEF8] overflow-hidden bg-white">
      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#64748B] bg-[#F8FAFC] border-b border-[#E8EEF8]">
        {title}
      </div>
      <pre className="p-3 text-xs overflow-auto max-h-56 text-[#0F172A] whitespace-pre-wrap break-all">
        {value == null ? "null" : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function ActionButton({ children, onClick, disabled, primary, danger }) {
  const base =
    "rounded-lg px-3 py-2 text-sm font-semibold transition disabled:opacity-50";
  if (primary)
    return (
      <button
        type="button"
        className={`${base} bg-primary1 text-white hover:brightness-110`}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </button>
    );
  if (danger)
    return (
      <button
        type="button"
        className={`${base} bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]`}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </button>
    );
  return (
    <button
      type="button"
      className={`${base} bg-white border border-[#CBD5E1] text-[#334155] hover:bg-[#F8FAFC]`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
